/**
 * ============================================
 * SERVIÇO DE API - PACK CINEMATOGRÁFICO
 * ============================================
 * * Preparado para deploy: Frontend (Vercel) + Backend (Render/Railway)
 * Usa REACT_APP_API_URL para todas as chamadas
 */

// URL da API do backend - configurada via variável de ambiente
const API_URL = process.env.REACT_APP_API_URL || '';

console.log("✅ API_URL carregada:", API_URL);

/**
 * Dispara download de um arquivo.
 * - R2 presigned URL: abre diretamente (Content-Disposition: attachment força o download sem navegar).
 * - Cloudinary (fl_attachment): mesma abordagem, o servidor já força o download.
 */
function _triggerDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Retorna o token de usuário do localStorage
 */
function getUserToken() {
  return localStorage.getItem('user_token');
}

/**
 * Retorna o token de admin do localStorage
 */
function getAdminToken() {
  return localStorage.getItem('admin_token');
}

/**
 * Função auxiliar para fazer requisições
 * @param {string} endpoint - Endpoint da API (sem /api)
 * @param {Object} options - Opções do fetch
 * @param {string} authType - Tipo de auth: 'none', 'user', 'admin'
 */
async function fetchAPI(endpoint, options = {}, authType = 'none') {
  const url = `${API_URL}/api${endpoint}`;

  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  if (authType === 'user') {
    const token = getUserToken();
    if (token) defaultHeaders['Authorization'] = `Bearer ${token}`;
  } else if (authType === 'admin') {
    const token = getAdminToken();
    if (token) defaultHeaders['Authorization'] = `Bearer ${token}`;
  } else if (authType === 'auto') {
    const token = getUserToken() || getAdminToken();
    if (token) defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers: { ...defaultHeaders, ...options.headers },
  });

  // Sessão expirada ou token inválido — redireciona para login sem navegar para página preta
  if (response.status === 401 || response.status === 403) {
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData.detail || '';
    const isAuthError = detail.includes('Token') || detail.includes('token') ||
      detail.includes('expirado') || detail.includes('inválido') ||
      detail.includes('fornecido') || response.status === 401;
    if (isAuthError && authType !== 'none') {
      localStorage.removeItem('user_token');
      window.dispatchEvent(new CustomEvent('auth:expired', { detail: detail }));
      throw new Error('session_expired');
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Erro ${response.status}`);
  }

  if (response.status === 204) return null;

  return response.json();
}

/**
 * API Principal
 */
export const api = {
  // ============================================
  // AUTENTICAÇÃO DE USUÁRIO
  // ============================================

  async login(email, password) {
    const response = await fetchAPI('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    // Salva token automaticamente
    if (response.access_token) {
      localStorage.setItem('user_token', response.access_token);
    }

    return response;
  },

  async getMe() {
    return fetchAPI('/auth/me', {}, 'auto');
  },

  logout() {
    localStorage.removeItem('user_token');
  },

  hasUserToken() {
    return !!getUserToken();
  },

  async validateSetupToken(token) {
    return fetchAPI(`/auth/validate-setup-token?token=${encodeURIComponent(token)}`);
  },

  async setPassword(token, password) {
    return fetchAPI('/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },

  // ============================================
  // PACKS / CENAS
  // ============================================

  /**
   * Lista todos os packs (cenas)
   */
  async getPacks(filters = {}) {
    const params = new URLSearchParams();

    if (filters.collection) params.append('collection', filters.collection);
    if (filters.tag) params.append('tag', filters.tag);
    if (filters.search) params.append('search', filters.search);
    if (filters.favorite !== undefined) params.append('favorite', filters.favorite);
    if (filters.orientation) params.append('orientation', filters.orientation);

    // ADICIONADO: Filtro por conta (Cenas A/B ou VIP C)
    if (filters.storage_account) params.append('storage_account', filters.storage_account);

    const queryString = params.toString();
    const endpoint = queryString ? `/packs?${queryString}` : '/packs';

    return fetchAPI(endpoint, {}, 'auto');
  },

  /**
   * Busca um pack específico
   */
  async getPack(id) {
    return fetchAPI(`/packs/${id}`);
  },

  /**
   * Executa o download de uma cena passando o token de autenticação
   * Resolve o problema de download bloqueado pelo servidor
   */
  async downloadPack(packId) {
    const data = await fetchAPI(`/packs/${packId}/download`, {}, 'user');
    if (data?.url) {
      _triggerDownload(data.url, data.filename);
    }
    return true;
  },

  async createPack(packData) {
    return fetchAPI('/packs', {
      method: 'POST',
      body: JSON.stringify(packData),
    }, 'admin');
  },

  async updatePack(id, packData) {
    return fetchAPI(`/packs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(packData),
    }, 'admin');
  },

  async deletePack(id) {
    return fetchAPI(`/packs/${id}`, {
      method: 'DELETE',
    }, 'admin');
  },

  async bulkDeletePacks(ids) {
    return fetchAPI('/admin/packs/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }, 'admin');
  },

  async toggleFavorite(id) {
    return fetchAPI(`/packs/${id}/favorite`, {
      method: 'PATCH',
    });
  },

  // ============================================
  // FAVORITOS
  // ============================================

  /**
   * Adiciona pack aos favoritos
   */
  async addFavorite(packId) {
    return fetchAPI(`/favorites/${packId}`, {
      method: 'POST',
    }, 'user');
  },

  /**
   * Remove pack dos favoritos
   */
  async removeFavorite(packId) {
    return fetchAPI(`/favorites/${packId}`, {
      method: 'DELETE',
    }, 'user');
  },

  /**
   * Retorna lista de IDs dos packs favoritados
   */
  async getFavorites() {
    return fetchAPI('/favorites', {}, 'user');
  },

  /**
   * Retorna detalhes completos dos packs favoritados
   */
  async getFavoritePacks() {
    return fetchAPI('/favorites/packs', {}, 'user');
  },

  getApiUrl() {
    return API_URL;
  },

  // ============================================
  // COLEÇÕES E TAGS
  // ============================================

  async getCollections() {
    return fetchAPI('/collections', {}, 'auto');
  },

  async getTags() {
    return fetchAPI('/tags', {}, 'auto');
  },

  // ============================================
  // UPLOAD DE VÍDEO (ADMIN)
  // ============================================

  async uploadVideo(file, conta = 'A') {
    const token = getAdminToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/api/upload-video?conta=${conta}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Erro ao enviar vídeo');
    }

    return response.json();
  },

  async uploadThumbnail(file) {
    const token = getAdminToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/api/upload-thumbnail`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Erro ao enviar thumbnail');
    }

    return response.json();
  },

  // ============================================
  // ✅ NOVO: CONTA D - MÚSICAS VIRAIS (ADMIN + USUÁRIO)
  // (adicionado sem mexer no que já funciona)
  // ============================================

  /**
   * ADMIN: Lista músicas no painel admin
   * Endpoint: GET /api/admin/sounds
   */
  async listSoundsAdmin() {
    return fetchAPI('/admin/sounds', {}, 'admin');
  },

  /**
   * ADMIN: Upload de Áudio (Conta D)
   * Endpoint: POST /api/upload-audio
   */
  async uploadAudio(file) {
    const token = getAdminToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/api/upload-audio`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Erro ao enviar áudio');
    }

    return response.json();
  },

  async uploadAudioR2(file) {
    const token = getAdminToken();
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/upload-audio-r2`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Erro ao enviar música para R2');
    }
    return response.json();
  },

  /**
   * ADMIN: Cria música no banco (Mongo)
   * Endpoint: POST /api/sounds
   */
  async createSound(soundData) {
    return fetchAPI('/sounds', {
      method: 'POST',
      body: JSON.stringify(soundData),
    }, 'admin');
  },

  async updateSound(soundId, soundData) {
    return fetchAPI(`/sounds/${soundId}`, {
      method: 'PATCH',
      body: JSON.stringify(soundData),
    }, 'admin');
  },

  /**
   * ADMIN: Deleta música
   * Endpoint: DELETE /api/sounds/:id
   */
  async deleteSound(soundId) {
    return fetchAPI(`/sounds/${soundId}`, {
      method: 'DELETE',
    }, 'admin');
  },

  /**
   * USUÁRIO: Lista músicas (TRAVA 7 DIAS no backend)
   * Endpoint: GET /api/sounds
   */
  async getSounds(filters = {}) {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.tag) params.append('tag', filters.tag);
    if (filters.genre) params.append('genre', filters.genre);
    if (filters.mood) params.append('mood', filters.mood);
    if (filters.usage_type) params.append('usage_type', filters.usage_type);
    if (filters.energy_level) params.append('energy_level', filters.energy_level);
    if (filters.sort) params.append('sort', filters.sort);

    const queryString = params.toString();
    const endpoint = queryString ? `/sounds/search?${queryString}` : '/sounds';
    return fetchAPI(endpoint, {}, 'user');
  },

  /**
   * USUÁRIO: Download de música (TRAVA 7 DIAS no backend)
   * R2: backend faz streaming → blob same-origin → diálogo salvar
   * Cloudinary: redirect fl_attachment
   */
  async downloadSound(soundId) {
    const token = getUserToken();
    const response = await fetch(`${API_URL}/api/sounds/${soundId}/download`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('user_token');
        window.dispatchEvent(new CustomEvent('auth:expired', { detail: err.detail || '' }));
        throw new Error('session_expired');
      }
      throw new Error(err.detail || 'Erro ao baixar música');
    }

    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (data?.url) _triggerDownload(data.url, data.filename || 'musica');
    } else {
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const fnMatch = disposition.match(/filename="([^"]+)"/);
      const filename = fnMatch ? fnMatch[1] : 'musica.mp3';
      const blobUrl = URL.createObjectURL(blob);
      _triggerDownload(blobUrl, filename);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    }
    return true;
  },



  // ============================================
  // ADMIN - AUTENTICAÇÃO LEGADA
  // ============================================

  async adminLogin(username, password) {
    const response = await fetch(`${API_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Erro ao fazer login');
    }

    const data = await response.json();
    if (data.token) {
      localStorage.setItem('admin_token', data.token);
    }
    return data;
  },

  async verifyAdminToken() {
    const token = getAdminToken();
    if (!token) return false;
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return response.ok;
    } catch {
      return false;
    }
  },

  adminLogout() {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
  },

  async getAdminReport(month, year) {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    return fetchAPI(`/admin/report?${params.toString()}`, {}, 'admin');
  },

  // ============================================
  // ADMIN - GERENCIAMENTO DE USUÁRIOS
  // ============================================

  async getUsers() {
      return fetchAPI('/admin/users', {}, 'admin');
    },

  async previewAccessMigration(limit = 500, userEmail = '') {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (userEmail) params.set('user_email', userEmail);
      return fetchAPI(`/admin/access-migration/preview?${params.toString()}`, {}, 'admin');
    },

  async applyAccessMigration(limit = 500, userEmail = '') {
      return fetchAPI('/admin/access-migration/apply', {
        method: 'POST',
        body: JSON.stringify({ limit, user_email: userEmail || null }),
      }, 'admin');
    },

  async toggleVipStatus(userId) {
      return fetchAPI(`/admin/users/${userId}/toggle-vip`, {
        method: 'PATCH',
      }, 'admin');
  },

  async toggleCourseStatus(userId) {
    return fetchAPI(`/admin/users/${userId}/toggle-course`, {
      method: 'PATCH',
    }, 'admin');
  },

  async toggleAIStatus(userId) {
    return fetchAPI(`/admin/users/${userId}/toggle-ai`, {
      method: 'PATCH',
    }, 'admin');
  },

  async createUser(email) {
    return fetchAPI('/admin/users/create', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }, 'admin');
  },

  async createUserManual(email, password) {
    return fetchAPI('/admin/users/create-manual', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }, 'admin');
  },

  async resetUserPassword(userId, password) {
    return fetchAPI(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }, 'admin');
  },

  async resendSetupEmail(userId) {
    return fetchAPI(`/admin/users/${userId}/resend-email`, {
      method: 'POST',
    }, 'admin');
  },

  async deleteUser(userId) {
    return fetchAPI(`/admin/users/${userId}`, {
      method: 'DELETE',
    }, 'admin');
  },

  // ============================================
  // SFX - EFEITOS SONOROS (Conta E)
  // ============================================

  /**
   * USUÁRIO: Lista SFX (TRAVA 7 DIAS no backend)
   * Endpoint: GET /api/sfx
   */
  async getSFX(filters = {}) {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/sfx?${queryString}` : '/sfx';
    
    return fetchAPI(endpoint, {}, 'user');
  },

  /**
   * USUÁRIO: Lista categorias de SFX com contagem
   * Endpoint: GET /api/sfx/categories
   */
  async getSFXCategories() {
    return fetchAPI('/sfx/categories', {}, 'user');
  },

  /**
   * USUÁRIO: Verifica se tem acesso aos SFX
   * Endpoint: GET /api/sfx/access
   */
  async checkSFXAccess() {
    return fetchAPI('/sfx/access', {}, 'user');
  },

  /**
   * USUÁRIO: Download de SFX (TRAVA 7 DIAS no backend)
   * Endpoint: GET /api/sfx/:id/download (RedirectResponse)
   */
  async downloadSFX(sfxId) {
    const data = await fetchAPI(`/sfx/${sfxId}/download`, {}, 'user');
    if (data?.url) {
      _triggerDownload(data.url, data.filename);
    }
    return true;
  },

  async getOverlays(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return fetchAPI(`/overlays${params ? '?' + params : ''}`, {}, 'user');
  },

  async downloadOverlay(overlayId) {
    const data = await fetchAPI(`/overlays/${overlayId}/download`, {}, 'user');
    if (data?.url) {
      _triggerDownload(data.url, data.filename);
    }
    return true;
  },

  /**
   * USUÁRIO: Registra play de SFX
   * Endpoint: POST /api/sfx/:id/play
   */
  async trackSFXPlay(sfxId) {
    return fetchAPI(`/sfx/${sfxId}/play`, {
      method: 'POST',
    }, 'user');
  },

  /**
   * ADMIN: Lista todos os SFX
   * Endpoint: GET /api/admin/sfx
   */
  async listSFXAdmin() {
    return fetchAPI('/admin/sfx', {}, 'admin');
  },

  /**
   * ADMIN: Cria SFX no banco
   * Endpoint: POST /api/sfx
   */
  async createSFX(sfxData) {
    return fetchAPI('/sfx', {
      method: 'POST',
      body: JSON.stringify(sfxData),
    }, 'admin');
  },

  /**
   * ADMIN: Deleta SFX
   * Endpoint: DELETE /api/sfx/:id
   */
  async deleteSFX(sfxId) {
    return fetchAPI(`/sfx/${sfxId}`, {
      method: 'DELETE',
    }, 'admin');
  },

  /**
   * ADMIN: Upload de arquivo de áudio SFX (Conta E)
   * Endpoint: POST /api/upload-sfx
   */
  async uploadSFX(file) {
    const token = getAdminToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/api/upload-sfx`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Erro ao enviar SFX');
    }

    return response.json();
  },

  // ============================================
  // HEALTH CHECK
  // ============================================

  async healthCheck() {
    return fetchAPI('/health');
  },
};

// Exporta fetchAPI para uso direto em componentes
export { fetchAPI };

export default api;
